import type {
  CreateWorkDayRequest,
  CreateWorkDayResponse,
  GetWorkDaysResponse,
  GetWorkDaysStatsResponse,
  GetAllWorkDaysResponse,
  GetEmployeesResponse,
  WorkStatus,
} from "./types"

/**
 * API Client for Remote Tracker Backend
 * 
 * Handles all HTTP requests to the backend API with Keycloak authentication
 */

// API base URL - empty string for same-origin requests
// In development: Vite dev server proxies /api/* to backend
// In production: nginx reverse proxy forwards /api/* to backend
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "https://remote.data.smartville.gr/"

/**
 * API Error class for structured error handling
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public response?: any
  ) {
    super(message)
    this.name = "ApiError"
  }
}

/**
 * Get authorization header with Keycloak JWT token
 */
function getAuthHeader(token: string): HeadersInit {
  return {
    "Authorization": `Bearer ${token}`,
    "Content-Type": "application/json",
  }
}

/**
 * Handle API response and errors
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T
  }

  const data = await response.json().catch(() => ({}))

  if (!response.ok) {
    throw new ApiError(
      data.error || `HTTP ${response.status}: ${response.statusText}`,
      response.status,
      data
    )
  }

  return data as T
}

/**
 * Remote Tracker API Client
 */
export class RemoteTrackerApi {
  /**
   * Create or update a work day entry
   * 
   * @param token - Keycloak JWT token
   * @param userId - Employee's UUID
   * @param request - Work day details
   * @returns Created/updated work day
   */
  static async createOrUpdateWorkDay(
    token: string,
    userId: string,
    request: CreateWorkDayRequest
  ): Promise<CreateWorkDayResponse> {
    const response = await fetch(
      `${API_BASE_URL}/api/employees/${userId}/work-days`,
      {
        method: "POST",
        headers: getAuthHeader(token),
        body: JSON.stringify(request),
      }
    )

    return handleResponse<CreateWorkDayResponse>(response)
  }

  /**
   * Delete a work day entry (revert to office status)
   * 
   * @param token - Keycloak JWT token
   * @param userId - Employee's UUID
   * @param date - Date in YYYY-MM-DD format
   */
  static async deleteWorkDay(
    token: string,
    userId: string,
    date: string
  ): Promise<void> {
    const response = await fetch(
      `${API_BASE_URL}/api/employees/${userId}/work-days/${date}`,
      {
        method: "DELETE",
        headers: getAuthHeader(token),
      }
    )

    return handleResponse<void>(response)
  }

  /**
   * Get work days for an employee for a specific month
   * 
   * @param token - Keycloak JWT token
   * @param userId - Employee's UUID
   * @param year - Year (e.g., 2025)
   * @param month - Month (1-12)
   * @returns Work days for the month
   */
  static async getWorkDays(
    token: string,
    userId: string,
    year: number,
    month: number
  ): Promise<GetWorkDaysResponse> {
    const url = `${API_BASE_URL}/api/employees/${userId}/work-days?year=${year}&month=${month}`

    
    const response = await fetch(url, {
      method: "GET",
      headers: getAuthHeader(token),
    })

    const data = await handleResponse<GetWorkDaysResponse>(response)

    
    return data
  }

  /**
   * Get work day statistics for an employee for a specific month
   * 
   * @param token - Keycloak JWT token
   * @param userId - Employee's UUID
   * @param year - Year (e.g., 2025)
   * @param month - Month (1-12)
   * @returns Statistics breakdown
   */
  static async getWorkDaysStats(
    token: string,
    userId: string,
    year: number,
    month: number
  ): Promise<GetWorkDaysStatsResponse> {
    const response = await fetch(
      `${API_BASE_URL}/api/employees/${userId}/work-days/stats?year=${year}&month=${month}`,
      {
        method: "GET",
        headers: getAuthHeader(token),
      }
    )

    return handleResponse<GetWorkDaysStatsResponse>(response)
  }

  /**
   * Get all work days for all employees (Admin only)
   * 
   * @param token - Keycloak JWT token
   * @param year - Year (e.g., 2025)
   * @param month - Month (1-12)
   * @param filters - Optional filters
   * @returns All work days with filters applied
   */
  static async getAllWorkDays(
    token: string,
    year: number,
    month: number,
    filters?: {
      status?: WorkStatus
      userId?: string
    }
  ): Promise<GetAllWorkDaysResponse> {
    const params = new URLSearchParams({
      year: year.toString(),
      month: month.toString(),
    })

    if (filters?.status) {
      params.append("status", filters.status)
    }

    if (filters?.userId) {
      params.append("employeeId", filters.userId)
    }

    const url = `${API_BASE_URL}/api/work-days?${params.toString()}`

    const response = await fetch(url, {
      method: "GET",
      headers: getAuthHeader(token),
    })

    const data = await handleResponse<GetAllWorkDaysResponse>(response)

    
    return data
  }

  /**
   * Get all employees (Admin only)
   * 
   * @param token - Keycloak JWT token
   * @returns List of all employees
   */
  static async getAllEmployees(token: string): Promise<GetEmployeesResponse> {
    
    const response = await fetch(`${API_BASE_URL}/api/employees`, {
      method: "GET",
      headers: getAuthHeader(token),
    })

    const data = await handleResponse<any>(response)
    
    // Handle different response formats
    // Some backends return { employees: [...] }
    // Others might return just [...]
    if (Array.isArray(data)) {
      return { employees: data }
    } else if (data && Array.isArray(data.employees)) {
      return data as GetEmployeesResponse
    } else {
      throw new Error('Invalid response format from /api/employees')
    }
  }
}

/**
 * Helper function to format date to YYYY-MM-DD
 */
export function formatDateForApi(date: Date): string {
  return date.toISOString().split("T")[0]
}

